import * as qs from 'qs';

import { IPagedQueryParams } from '@Shared/grid';
import { SetupIntent } from '@Shared/interfaces/Stripe';
import { IBillModel } from '@Shared/models';

import { Api as apiService } from '../api';

interface IBillListApiPayload {
  rows: IBillModel[];
  count: number;
}

export abstract class BillApi {
  static getAllBills(params?: IPagedQueryParams & { before: string; historical_billing: boolean }): Promise<IBillListApiPayload> {
    const searchParams = qs.stringify(params);

    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/bills?${searchParams}`));
  }

  static getBill(bill_id: string): Promise<IBillModel> {
    return apiService.responseToJSONAndToastError(apiService.get('/api/web/bill/' + bill_id));
  }

  static getBillingCredits(): Promise<any> {
    return apiService.responseToJSONAndToastError(apiService.get('/api/web/bill_user_credits'));
  }

  static getSubscription(): Promise<{ subscription: any }> {
    return apiService.responseToJSONAndToastError(apiService.get('/api/web/bills/subscription'));
  }

  static createSubscription(billingPlan: string, createSetupIntent = false): Promise<{ subscription: any; checkoutSession?: any }> {
    return apiService.responseToJSONAndToastError(apiService.post('/api/web/bills/subscription', { billingPlan, createSetupIntent }));
  }

  static getInProgressSetupIntent(): Promise<SetupIntent | undefined> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/bills/subscription/setup`));
  }

  static cancelSetupIntent(setupIntentId: string): Promise<SetupIntent | undefined> {
    return apiService.responseToJSONAndToastError(apiService.delete(`/api/web/bills/subscription/setup/${setupIntentId}`));
  }

  static createSetupIntent(subscriptionId?: string): Promise<{ data: any }> {
    const searchParams = qs.stringify({ subscription_id: subscriptionId });
    return apiService.responseToJSONAndToastError(apiService.post(`/api/web/bills/subscription/setup?${searchParams}`, {}));
  }

  static updatePaymentMethod(paymentMethodId: string, subscriptionId?: string): Promise<{ subscription: any }> {
    const searchParams = qs.stringify({ subscription_id: subscriptionId });
    return apiService.responseToJSONAndToastError(
      apiService.post(`/api/web/bills/subscription/paymentMethod?${searchParams}`, {
        paymentMethodId: paymentMethodId
      })
    );
  }

  static getEstimatedTax(
    subscriptionAmount: number,
    implementationAmount: number = 0
  ): Promise<{ subscriptionTax: number; implementationTax: number }> {
    return apiService.responseToJSONAndToastError(
      apiService.get(`/api/web/bills/estimated_tax?subscription_amount=${subscriptionAmount}&implementation_amount=${implementationAmount}`)
    );
  }

  /**
   * Retrieve Stripe pricing tiers for a given plan ID
   * @param planId - The billing plan ID to fetch pricing tiers for
   * @returns Promise containing array of Stripe pricing tiers from the usage product
   */
  static getStripePriceTiersByBillingPlanId(planId: string): Promise<any[]> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/bills/stripe/tiers/${planId}`));
  }
}
